/**
 * tpEnums.ts — dropdown vocabularies for the flat filter UI. The closed enums (seniority, departments,
 * study levels, company sizes, proficiency, manager/IC, job-hopper) mirror the engine's /api/enums and
 * the planner's enum lists. INDUSTRIES is the fallback list — replaced at runtime by GET /api/enums.
 */

export type Opt = { value: string; label: string; description?: string };

const titleCase = (s: string) => s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const opt = (vals: readonly string[], labels?: Record<string, string>): Opt[] => vals.map((v) => ({ value: v, label: labels?.[v] || titleCase(v) }));

export const TITLE_SENIORITY = ['intern', 'junior', 'mid', 'senior', 'lead', 'staff', 'principal', 'manager', 'director', 'vp', 'executive', 'founder'] as const;
export const SENIORITY_OPTS = opt(TITLE_SENIORITY, { vp: 'VP', executive: 'Executive' });

export const DEPARTMENTS = [
    'engineering',
    'design',
    'product',
    'marketing',
    'sales',
    'operations',
    'finance',
    'hr',
    'legal',
    'research',
    'data',
    'support',
    'consulting',
    'education',
    'healthcare',
    'administrative',
    'other',
] as const;
export const DEPARTMENT_OPTS = opt(DEPARTMENTS, { hr: 'HR' });

export const STUDY_LEVELS = ['high_school', 'vocational', 'certificate', 'associate', 'bachelor', 'master', 'mba', 'doctorate', 'professional', 'other'] as const;
export const STUDY_LEVEL_OPTS = opt(STUDY_LEVELS, { high_school: 'High School', mba: 'MBA', professional: 'Professional (JD/MD/LLB)' });

export const COMPANY_SIZES = ['1-10', '11-50', '51-200', '201-500', '501-1000', '1001-5000', '5001-10000', '10001+'] as const;
export const COMPANY_SIZE_OPTS = COMPANY_SIZES.map((v) => ({ value: v, label: `${v} employees` }));

export const PROFICIENCY_OPTS: Opt[] = [
    { value: 'any', label: 'Any proficiency' },
    { value: 'elementary', label: 'Elementary' },
    { value: 'intermediate', label: 'Intermediate' },
    { value: 'professional', label: 'Professional working' },
    { value: 'fluent', label: 'Fluent' },
    { value: 'native', label: 'Native / Bilingual' },
];

export const IS_MANAGER_OPTS: Opt[] = [
    { value: 'any', label: 'Any' },
    { value: '1', label: 'People manager' },
    { value: '0', label: 'Individual contributor' },
];

export const JOB_HOPPER_OPTS: Opt[] = [
    { value: 'any', label: 'Any' },
    { value: '0', label: 'Stable' },
    { value: '1', label: 'Frequent mover' },
];

export const INDUSTRIES_FALLBACK: Opt[] = [
    'Software Development',
    'IT Services and IT Consulting',
    'Financial Services',
    'Banking',
    'Capital Markets',
    'Investment Management',
    'Blockchain Services',
    'Insurance',
    'Management Consulting',
    'Hospitals and Health Care',
    'Biotechnology Research',
    'Manufacturing',
    'Retail',
    'Education',
    'Higher Education',
    'Media and Communications',
    'Gaming',
    'Real Estate',
    'Energy',
    'Telecommunications',
].map((v) => ({ value: v, label: v }));

/** Fields offered when composing an advanced boolean condition (value-based). */
export const BOOLEAN_FIELD_OPTS: Opt[] = [
    { value: 'skill', label: 'Skill' },
    { value: 'title', label: 'Job title' },
    { value: 'company', label: 'Company' },
    { value: 'industry', label: 'Industry' },
    { value: 'certification', label: 'Certification' },
    { value: 'field_of_study', label: 'Field of study' },
    { value: 'degree', label: 'Degree' },
    { value: 'university', label: 'University' },
    { value: 'language', label: 'Language' },
    { value: 'location_city', label: 'City' },
    { value: 'location_country', label: 'Country / region' },
    { value: 'department', label: 'Department' },
    { value: 'seniority', label: 'Seniority' },
];

export const lbl = (v: string, opts: Opt[]) => opts.find((o) => o.value === v)?.label || titleCase(v);
export const fieldLabel = (field: string) => BOOLEAN_FIELD_OPTS.find((o) => o.value === field)?.label || titleCase(field);
export const cap = titleCase;
